<template>
  <modal-frame size="md" esc-close>
    <div class="modal" role="dialog" aria-modal="true">
      <modal-header :title="$t('dignities.modal-manage-dignities.title')" />

      <modal-content>
        <MultiSelect
          v-model="selectedDignities"
          :options="dignitiesStore.dignities"
          :loading="dignitiesStore.loading"
          :disabled="!dignitiesStore.dignities.length"
          :option-label="`name.${locale}`"
          :placeholder="$t('dignities.modal-manage-dignities.dignities-placeholder')"
          data-key="id"
          display="chip"
          class="md:max-w-full" />
      </modal-content>

      <modal-footer>
        <Button
          :loading="isUpdateDignitiesLoading"
          :disabled="isUpdateDignitiesLoading || !hasDignitiesChanged"
          :label="$t('dignities.modal-manage-dignities.save-button')"
          class="w-full"
          rounded
          size="large"
          @click="onSaveDignitiesClicked" />
      </modal-footer>
    </div>
  </modal-frame>
</template>

<script setup lang="ts">
import isEqual from 'lodash/isEqual';
import cloneDeep from 'lodash/cloneDeep';
import { ApiError, type DoctorSpeciality } from '~/api/generated';

const { t, locale } = useI18n();

const { extractApiError } = useApiUtils();

const { success } = useCustomToasts();

const userStore = useUserStore();

const dignitiesStore = useDignitiesStore();

const selectedDignities = ref<DoctorSpeciality[]>([]);

onMounted(() => {
  selectedDignities.value = cloneDeep(
    dignitiesStore.dignities.filter(({ id }) => userStore.user?.settings?.dignities?.includes(id) || false)
  );
});

const $modal = inject('$modal', {
  close: (success: boolean) => success
});

const hasDignitiesChanged = computed(() => {
  const current = userStore.user?.settings?.dignities || [];
  const updated = selectedDignities.value.map(({ id }) => id);

  return !isEqual(current, updated);
});

const { execute: updateDignities, isLoading: isUpdateDignitiesLoading } = useAsyncState(
  userStore.updateUser,
  undefined,
  {
    immediate: false,
    onSuccess: () => {
      $modal.close(true);

      success(t('dignities.notifications.dignities-updated'));
    },
    onError: (apiError) => {
      extractApiError(apiError as ApiError, 'dignities.notifications.dignities-update-failed');
    }
  }
);

const onSaveDignitiesClicked = async () => {
  await updateDignities(0, {
    requestBody: {
      dignities: selectedDignities.value.map(({ id }) => id)
    }
  });
};
</script>
